import React, { useState, useRef, useEffect } from 'react';
import Icon from 'components/Icon';

const ToolTip = ({
  info,
  content,
  children,
  maxWidth,
  iconColor,
  iconSize,
  customStyle = {},
  placement = 'auto', // 'top', 'bottom', 'left', 'right', 'auto'
  offset = 10,
  delay = 0,
  arrow = true,
  preservePosition = false, // Don't interfere with child positioning
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [finalPlacement, setFinalPlacement] = useState('bottom');
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    // When using preservePosition, get bounds from the first child element
    const targetElement = preservePosition && triggerRef.current.children.length > 0 
      ? triggerRef.current.children[0] 
      : triggerRef.current;
      
    const trigger = targetElement.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Available space in each direction
    const spaceTop = trigger.top;
    const spaceBottom = viewport.height - trigger.bottom;
    const spaceLeft = trigger.left;
    const spaceRight = viewport.width - trigger.right;

    let bestPlacement = placement;
    
    // Auto-determine best placement
    if (placement === 'auto') {
      const spaces = {
        top: spaceTop,
        bottom: spaceBottom,
        left: spaceLeft,
        right: spaceRight,
      };
      bestPlacement = Object.keys(spaces).reduce((a, b) => 
        spaces[a] > spaces[b] ? a : b
      );
    }

    // Calculate position based on placement
    let newPosition = { top: 0, left: 0 };
    let adjustedPlacement = bestPlacement;

    switch (bestPlacement) {
      case 'top':
        if (spaceTop < tooltip.height + offset) {
          adjustedPlacement = 'bottom';
          newPosition = {
            top: trigger.bottom + offset,
            left: trigger.left + (trigger.width - tooltip.width) / 2,
          };
        } else {
          newPosition = {
            top: trigger.top - tooltip.height - offset,
            left: trigger.left + (trigger.width - tooltip.width) / 2,
          };
        }
        break;
      
      case 'bottom':
      default:
        if (spaceBottom < tooltip.height + offset) {
          adjustedPlacement = 'top';
          newPosition = {
            top: trigger.top - tooltip.height - offset,
            left: trigger.left + (trigger.width - tooltip.width) / 2,
          };
        } else {
          newPosition = {
            top: trigger.bottom + offset,
            left: trigger.left + (trigger.width - tooltip.width) / 2,
          };
        }
        break;
      
      case 'left':
        if (spaceLeft < tooltip.width + offset) {
          adjustedPlacement = 'right';
          newPosition = {
            top: trigger.top + (trigger.height - tooltip.height) / 2,
            left: trigger.right + offset,
          };
        } else {
          newPosition = {
            top: trigger.top + (trigger.height - tooltip.height) / 2,
            left: trigger.left - tooltip.width - offset,
          };
        }
        break;
      
      case 'right':
        if (spaceRight < tooltip.width + offset) {
          adjustedPlacement = 'left';
          newPosition = {
            top: trigger.top + (trigger.height - tooltip.height) / 2,
            left: trigger.left - tooltip.width - offset,
          };
        } else {
          newPosition = {
            top: trigger.top + (trigger.height - tooltip.height) / 2,
            left: trigger.right + offset,
          };
        }
        break;
    }

    // Constrain to viewport bounds with fallback
    if (newPosition.left < 8 || newPosition.left + tooltip.width > viewport.width - 8) {
      // If horizontal centering fails, align to trigger left edge with some offset
      newPosition.left = Math.max(8, Math.min(trigger.left, viewport.width - tooltip.width - 8));
    }
    
    if (newPosition.top < 8 || newPosition.top + tooltip.height > viewport.height - 8) {
      // If vertical positioning fails, use a safe fallback
      newPosition.top = Math.max(8, Math.min(newPosition.top, viewport.height - tooltip.height - 8));
    }

    setPosition(newPosition);
    setFinalPlacement(adjustedPlacement);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (delay > 0) {
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, delay);
    } else {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShowTooltip(false);
  };

  useEffect(() => {
    if (showTooltip) {
      // Small delay to ensure tooltip is rendered before calculating position
      const timeoutId = setTimeout(() => {
        calculatePosition();
      }, 16); // One frame delay
      
      return () => clearTimeout(timeoutId);
    }
  }, [showTooltip]);

  useEffect(() => {
    const handleResize = () => {
      if (showTooltip) {
        calculatePosition();
      }
    };

    const handleScroll = () => {
      if (showTooltip) {
        calculatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [showTooltip]);

  const getArrowStyle = () => {
    if (!arrow) return {};

    const arrowSize = 6;
    const arrowStyles = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    };

    switch (finalPlacement) {
      case 'top':
        return {
          ...arrowStyles,
          bottom: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
          borderColor: 'white transparent transparent transparent',
        };
      case 'bottom':
        return {
          ...arrowStyles,
          top: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent white transparent',
        };
      case 'left':
        return {
          ...arrowStyles,
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: 'transparent transparent transparent white',
        };
      case 'right':
        return {
          ...arrowStyles,
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: 'transparent white transparent transparent',
        };
      default:
        return {};
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={preservePosition ? {
          // Completely transparent wrapper that doesn't interfere
          display: 'contents',
          cursor: 'inherit',
        } : {
          display: 'inline-flex',
          alignItems: 'center',
          cursor: 'pointer',
        }}
      >
        {info && (
          <Icon
            icon="info"
            color={iconColor ?? '#00001f'}
            size={iconSize ?? '20px'}
            style={{ marginLeft: '5px' }}
          />
        )}
        {children}
      </span>
      
      {showTooltip && (
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            top: position.top || -9999, // Hide off-screen initially
            left: position.left || -9999,
            backgroundColor: 'white',
            color: 'black',
            padding: '12px 16px',
            borderRadius: '6px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '1.4',
            maxWidth: maxWidth || '300px',
            minWidth: '80px',
            width: 'max-content',
            zIndex: 9999,
            opacity: (position.top !== 0 || position.left !== 0) ? 1 : 0, // Only show when positioned
            transition: 'opacity 0.2s ease-in-out',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            wordWrap: 'break-word',
            hyphens: 'auto',
            visibility: (position.top !== 0 || position.left !== 0) ? 'visible' : 'hidden',
            ...customStyle,
          }}
        >
          {content}
          {arrow && <div style={getArrowStyle()} />}
        </div>
      )}
    </>
  );
};

export default ToolTip;